import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import JobService from "../../../../services/JobService";
import CategoryService from "../../../../services/CategoryService";
import UserService from "../../../../services/UserService";
import TaskService from "../../../../services/TaskService";
import CustomerService from "../../../../services/CustomerService";
import "rsuite/dist/rsuite.min.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TimeDropdown from '../../shared/TimeDropdown';
import { colors } from "../../../config/colors";

import JobHeader from "../Components/JobHeader";
import JobLoader from "../Components/JobLoader";
import { getJobTitleLabel, getJobTitleLabelPlural, getChargeOutRateTitle, getCostOfSupplyTitle } from "../../../utils/jobTitleLabel";
import JobCalendar from "../JobCalendar";

const DEFAULT_FORM_DATA = {
  jobTitle: "",
  jobStatus: "PENDING",
  customer: null,
  jobDate: "",
  jobTime: {
    start: "",
    end: ""
  },
  jobCategories: [],
  assignedEmployee: null,
  subtotal: 0,
  taxes: 0,
  totalCost: 0,
  taxPercentage: 10,
  costOfSupply: '',
  showCostToEmployee: false,
};

const JOB_STATUS_OPTIONS = [
  { label: "Pending", value: "PENDING" },
  { label: "Driving", value: "DRIVING" },
  { label: "Arrived", value: "ARRIVED" },
  { label: "Started", value: "STARTED" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Skipped", value: "SKIPPED" },
  { label: "Cancelled", value: "CANCELLED" },
  { label: "Failed", value: "FAILED" },
];

const CreateJob = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const location = useLocation();
  const jobLabel = getJobTitleLabel();
  const jobPluralLabel = getJobTitleLabelPlural();
  const chargeOutRateTitle = getChargeOutRateTitle();
  const costOfSupplyTitle = getCostOfSupplyTitle();

  const isEditMode = location.pathname.includes('edit-job');
  const isDraftMode = location.search.includes('draft=true');

  const [loading, setLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [historicalTasks, setHistoricalTasks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [taskSearchTerm, setTaskSearchTerm] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);
  const [isTaskDropdownOpen, setIsTaskDropdownOpen] = useState({});
  const [taskSearchTerms, setTaskSearchTerms] = useState({});
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [selectedTaskRows, setSelectedTaskRows] = useState([
    { id: 1, taskId: '', quantity: 1, isQuantityEnabled: true }
  ]);

  const [reoccurringJob, setReoccurringJob] = useState(null);

  // Get today's date in YYYY-MM-DD format for min date validation
  const today = new Date().toISOString().split("T")[0];

  // Common Input Styles - Add at the top of the component
  const inputClasses = "w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent";
  const labelClasses = "block text-sm font-medium text-accent-dark mb-2";
  const requiredStar = <span className="text-cta">*</span>;

  // Refs for dropdown containers
  const categoryDropdownRef = useRef(null);
  const customerDropdownRef = useRef(null);
  const employeeDropdownRef = useRef(null);
  const taskDropdownRefs = useRef({});

  // Add refs for the picker containers
  const datePickerRef = useRef(null);
  const startTimePickerRef = useRef(null);
  const endTimePickerRef = useRef(null);

  const [isStartTimeOpen, setIsStartTimeOpen] = useState(false);
  const [isEndTimeOpen, setIsEndTimeOpen] = useState(false);

  // Add state for validation messages
  const [validationMessage, setValidationMessage] = useState({
    jobTitle: '',
    taxPercentage: '',
    time: ''
  });

  const showTemporaryError = (field, message) => {
    setValidationMessage(prev => ({
      ...prev,
      [field]: message
    }));

    // Clear the message after 1 second
    setTimeout(() => {
      setValidationMessage(prev => ({
        ...prev,
        [field]: ''
      }));
    }, 2000);
  }
  const DRAFT_STORAGE_KEY = "job_draft_data";

  // Normalize and display API error messages from backend
  const showApiError = (error, fallbackMessage = "Something went wrong") => {
    if (!error || typeof error === "string") {
      toast.error(typeof error === "string" ? error : fallbackMessage);
      return;
    }

    const data = error.response?.data;

    // Case 1: validation errors as array [{ field, message }]
    if (Array.isArray(data)) {
      const messages = data
        .map((e) => (e?.message ? `${e.message}` : ""))
        .filter(Boolean);
      if (messages.length) {
        toast.error(messages.join("\n"));
        return;
      }
    }

    // Case 2: { error, details } where details may be array or string/object
    if (data) {
      const base = data.error || data.message;

      let detailsText = "";
      if (Array.isArray(data.details)) {
        const msgs = data.details
          .map((d) => (typeof d === "string" ? d : d?.message || ""))
          .filter(Boolean);
        detailsText = msgs.join("\n");
      } else if (typeof data.details === "string") {
        detailsText = data.details;
      }

      const finalMsg = [base, detailsText].filter(Boolean).join(": ");
      if (finalMsg) {
        toast.error(finalMsg);
        return;
      }
    }

    // Fallback
    toast.error(fallbackMessage);
  };

  const [showDraftModal, setShowDraftModal] = useState(false);

  // Add these with the other state declarations at the top of the component
  const [reoccurType, setReoccurType] = useState('do_not_repeat');
  const [customRepeatEvery, setCustomRepeatEvery] = useState(1);
  const [customRepeatUnit, setCustomRepeatUnit] = useState('day');
  const [customRepeatDays, setCustomRepeatDays] = useState(() => {
    const today = new Date().getDay();
    return Array(7).fill(false).map((_, index) => index === today);
  });
  const [repeatTill, setRepeatTill] = useState(null);

  // Add this state near other state declarations
  const [showReoccurringModal, setShowReoccurringModal] = useState(false);
  const [pendingJobData, setPendingJobData] = useState(null);
  const [showRecurrencePreview, setShowRecurrencePreview] = useState(false);
  const [previewCalendarDate, setPreviewCalendarDate] = useState(() => new Date());
  const [previewCalendarView, setPreviewCalendarView] = useState('month');

  // Add this state near other dropdown states
  const [isReoccurTypeDropdownOpen, setIsReoccurTypeDropdownOpen] = useState(false);

  // Tooltip visibility state for recurrence options
  const [recurrenceTooltip, setRecurrenceTooltip] = useState(null);

  // Add this ref with other refs
  const reoccurTypeDropdownRef = useRef(null);

  // Add this state near other dropdown states
  const [isCustomRepeatUnitDropdownOpen, setIsCustomRepeatUnitDropdownOpen] = useState(false);

  // Add this ref with other refs
  const customRepeatUnitDropdownRef = useRef(null);

  // Add this helper function with other utility functions near the top of the file
  const getDayName = (dayIndex) => {
    return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayIndex];
  };

  const getMaxRepeatDate = () => {
    const maxDate = formData.jobDate ? new Date(formData.jobDate) : new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    return maxDate;
  };

  const getRecurrencePreviewDates = () => {
    if (!formData.jobDate || !repeatTill) return [];

    const start = new Date(formData.jobDate);
    start.setHours(0, 0, 0, 0);

    const maxEnd = new Date(start);
    maxEnd.setMonth(maxEnd.getMonth() + 3);

    const end = new Date(repeatTill < maxEnd ? repeatTill : maxEnd);
    end.setHours(23, 59, 59, 999);

    if (end <= start) return [];

    if (reoccurType === 'daily') {
      const dates = [];
      const cursor = new Date(start);
      while (cursor <= end) {
        dates.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
      return dates;
    }

    if (reoccurType === 'weekly') {
      const dates = [];
      const targetDay = start.getDay();
      const cursor = new Date(start);
      while (cursor <= end) {
        if (cursor.getDay() === targetDay) dates.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
      return dates;
    }

    if (reoccurType === 'custom') {
      const interval = Math.max(1, parseInt(customRepeatEvery) || 1);
      if (customRepeatUnit === 'day') {
        const dates = [];
        const cursor = new Date(start);
        while (cursor <= end) {
          dates.push(new Date(cursor));
          cursor.setDate(cursor.getDate() + interval);
        }
        return dates;
      }
      if (customRepeatUnit === 'week') {
        const selectedDays = customRepeatDays
          .map((on, i) => on ? i : -1)
          .filter(d => d !== -1);
        if (selectedDays.length === 0) return [];
        const dates = [];
        const cursor = new Date(start);
        while (cursor <= end) {
          const weekStart = new Date(cursor);
          for (let d = 0; d < 7; d++) {
            const day = new Date(weekStart);
            day.setDate(weekStart.getDate() + d);
            day.setHours(0, 0, 0, 0);
            if (day >= start && day <= end && selectedDays.includes(day.getDay())) {
              dates.push(new Date(day));
            }
          }
          cursor.setDate(cursor.getDate() + interval * 7);
        }
        // de-dupe + sort
        return Array.from(new Set(dates.map(d => d.toISOString().slice(0, 10))))
          .map(s => new Date(s))
          .sort((a, b) => a.getTime() - b.getTime());
      }
      if (customRepeatUnit === 'month') {
        const dates = [];
        const cursor = new Date(start);
        while (cursor <= end) {
          dates.push(new Date(cursor));
          cursor.setMonth(cursor.getMonth() + interval);
        }
        return dates;
      }
    }

    return [];
  };

  const previewJobs = useMemo(() => {
    const dates = getRecurrencePreviewDates();
    if (!dates.length) return [];
    const startTime = formData?.jobTime?.start || null;
    const endTime = formData?.jobTime?.end || null;
    const title = formData?.jobTitle || `${jobLabel} (Preview)`;
    return dates.map((d) => ({
      _id: `preview-${d.toISOString().slice(0, 10)}`,
      title,
      status: "UPCOMING",
      scheduledDate: d.toISOString().slice(0, 10),
      scheduledStartTime: startTime,
      scheduledEndTime: endTime,
      isPreview: true,
    }));
  }, [
    formData?.jobDate,
    repeatTill,
    reoccurType,
    customRepeatEvery,
    customRepeatUnit,
    JSON.stringify(customRepeatDays),
    formData?.jobTime?.start,
    formData?.jobTime?.end,
    formData?.jobTitle,
    jobLabel,
  ]);

  // Estimate how many jobs will be auto-created based on current recurrence settings
  const getEstimatedJobCount = () => {
    if (!formData.jobDate || !repeatTill) return null;

    const start = new Date(formData.jobDate);
    start.setHours(0, 0, 0, 0);

    // Cap end at 3 months from job date
    const maxEnd = new Date(start);
    maxEnd.setMonth(maxEnd.getMonth() + 3);
    const end = repeatTill < maxEnd ? new Date(repeatTill) : maxEnd;
    end.setHours(23, 59, 59, 999);

    if (end <= start) return 0;

    if (reoccurType === 'daily') {
      const diffMs = end - start;
      return Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1; // inclusive of start
    }

    if (reoccurType === 'weekly') {
      // Repeats on the weekday of job date
      const targetDay = start.getDay();
      let count = 0;
      const cursor = new Date(start);
      while (cursor <= end) {
        if (cursor.getDay() === targetDay) count++;
        cursor.setDate(cursor.getDate() + 1);
      }
      return count;
    }

    if (reoccurType === 'custom') {
      const interval = Math.max(1, parseInt(customRepeatEvery) || 1);
      if (customRepeatUnit === 'day') {
        let count = 0;
        const cursor = new Date(start);
        while (cursor <= end) {
          count++;
          cursor.setDate(cursor.getDate() + interval);
        }
        return count;
      }
      if (customRepeatUnit === 'week') {
        const selectedDays = customRepeatDays
          .map((on, i) => on ? i : -1)
          .filter(d => d !== -1);
        if (selectedDays.length === 0) return 0;
        let count = 0;
        const cursor = new Date(start);
        // advance week by week using interval, within each week count matching days
        while (cursor <= end) {
          const weekStart = new Date(cursor);
          for (let d = 0; d < 7; d++) {
            const day = new Date(weekStart);
            day.setDate(weekStart.getDate() + d);
            if (day >= start && day <= end && selectedDays.includes(day.getDay())) count++;
          }
          cursor.setDate(cursor.getDate() + interval * 7);
        }
        return count;
      }
      if (customRepeatUnit === 'month') {
        let count = 0;
        const cursor = new Date(start);
        while (cursor <= end) {
          count++;
          cursor.setMonth(cursor.getMonth() + interval);
        }
        return count;
      }
    }

    return null;
  };

  const loadDraftFromLocalStorage = () => {
    try {
      const draftData = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (draftData) {
        const parsedData = JSON.parse(draftData);
        setFormData(parsedData.formData);
        setSelectedTaskRows(parsedData.selectedTaskRows);

        // Set search terms based on loaded data
        if (parsedData.formData.jobCategories && parsedData.formData.jobCategories.length > 0) {
          // No need to set the search term for multiple categories
        }
        if (parsedData.formData.customer) {
          setCustomerSearchTerm(parsedData.formData.customer.name || '');
        }
        if (parsedData.formData.assignedEmployee) {
          setEmployeeSearchTerm(parsedData.formData.assignedEmployee.name || '');
        }

        // Clear the draft after loading
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        toast.success("Draft loaded successfully");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error loading draft:", error);
      toast.error("Failed to load draft");
      return false;
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  };

  useEffect(() => {
    if (jobId) {
      const fetchJobDetails = async () => {
        try {
          setLoading(true);
          const response = await JobService.getJobById(jobId);
          if (response.data.success) {
            const job = response.data.data;

            // Format start time
            let startTime = job.scheduledStartTime;
            if (startTime && startTime.includes('T')) {
              const startDate = new Date(startTime);
              startTime = startDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              });
            }

            // Format end time
            let endTime = job.scheduledEndTime;
            if (endTime && endTime.includes('T')) {
              const endDate = new Date(endTime);
              endTime = endDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              });
            }

            setFormData({
              jobTitle: job.title,
              jobStatus: job?.status || "DRAFT",
              customer: job?.customer?.customerId ? {
                _id: job.customer.customerId,
                name: job.customer.name,
                email: job.customer.email,
                phoneNumber: job.customer.phoneNumber
              } : null,
              jobDate: isEditMode ? job.scheduledDate : "",
              jobTime: {
                start: isEditMode ? startTime : "",
                end: isEditMode ? endTime : ""
              },
              jobCategories: job?.categoryIds ? job.categoryIds : (Array.isArray(job?.categoryId) ? job.categoryId : (job?.categoryId ? [job.categoryId] : [])),
              assignedEmployee: job?.assignedEmployee?.employeeId ? {
                _id: job.assignedEmployee.employeeId,
                name: job.assignedEmployee.name,
                email: job.assignedEmployee.email,
                phone: job.assignedEmployee.phone,
                skills: job.assignedEmployee.skills
              } : null,
              subtotal: job?.cost?.subtotal || 0,
              taxes: job?.cost?.subtotal * job?.cost?.tax / 100,
              totalCost: job?.cost?.total || 0,
              taxPercentage: job?.cost?.tax || 10,
              costOfSupply: job?.cost?.costOfSupply || '',
              showCostToEmployee: job?.showCost || false
            });
            const selectedTasks = job.tasks.map(task => ({
              id: Date.now() + Math.random(),
              taskId: task.taskId,
              quantity: task.quantity,
              cost: task.cost,
              isQuantityEnabled: task.isQuantityEnabled !== undefined ? task.isQuantityEnabled : true
            }));
            setSelectedTaskRows(selectedTasks);
            setTaskSearchTerms(selectedTasks.reduce((acc, task) => {
              acc[task.id] = tasks.find(t => t._id === task.taskId)?.title || '';
              return acc;
            }, {}));
            setReoccurringJob(job.reoccurringId);
          } else {
            throw new Error("Failed to fetch job details");
          }
        } catch (error) {
          console.error("Error fetching job details:", error);
          toast.error("Failed to fetch job details");
        } finally {
          setLoading(false);
        }
      };
      fetchJobDetails();
    } else {
      // Reset form when switching to create mode
      setFormData(DEFAULT_FORM_DATA);
    }
    fetchCategories();
  }, [jobId]);

  useEffect(() => {
    fetchCategories();
    // fetchEmployees();
    fetchCustomers();
  }, []);

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Category dropdown
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
      // Customer dropdown
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setIsCustomerDropdownOpen(false);
      }
      // Employee dropdown
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(event.target)) {
        setIsEmployeeDropdownOpen(false);
      }
      // Task dropdowns
      Object.keys(taskDropdownRefs.current).forEach(key => {
        if (taskDropdownRefs.current[key] && !taskDropdownRefs.current[key].contains(event.target)) {
          setIsTaskDropdownOpen(prev => ({
            ...prev,
            [key]: false
          }));
        }
      });

      // Start time dropdown
      if (startTimePickerRef.current && !startTimePickerRef.current.contains(event.target)) {
        setIsStartTimeOpen(false);
      }
      // End time dropdown
      if (endTimePickerRef.current && !endTimePickerRef.current.contains(event.target)) {
        setIsEndTimeOpen(false);
      }

      // Reoccur type dropdown
      if (reoccurTypeDropdownRef.current && !reoccurTypeDropdownRef.current.contains(event.target)) {
        setIsReoccurTypeDropdownOpen(false);
      }

      // Custom repeat unit dropdown
      if (customRepeatUnitDropdownRef.current && !customRepeatUnitDropdownRef.current.contains(event.target)) {
        setIsCustomRepeatUnitDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await CategoryService.getCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories");
    }
  };

  const fetchEmployees = async () => {
    if (!formData.jobCategories || formData.jobCategories.length === 0) {
      return;
    }

    try {
      const filters = {
        page: 1,
        limit: 1000,
        search: employeeSearchTerm,
        role: 'EMPLOYEE',
        skills: formData.jobCategories && formData.jobCategories.length > 0
          ? formData.jobCategories.map(c => c._id).join(',')
          : ''
      };

      const response = await UserService.getUsers(filters);

      if (response.data.status === "success") {
        const { users } = response.data.data;
        setEmployees(users || []);

        if (formData.assignedEmployee) {
          const employeeExists = users.some(
            (emp) => emp._id === formData.assignedEmployee._id
          );
          if (!employeeExists) {
            setFormData((prev) => ({
              ...prev,
              assignedEmployee: "",
            }));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to fetch employees");
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await CustomerService.getCustomers(1, 100, "");
      if (response.data.success) {
        setCustomers(response.data.data || []);
        console.log(">>>> Customers:", response);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to fetch customers");
    }
  };

  // Add useEffect to refetch employees when category changes
  // Fetch employees and validate current assigned employee
  useEffect(() => {
    if (formData.jobCategories && formData.jobCategories.length > 0) {
      fetchEmployees();
    }

    // Reset assigned employee if they don't have ANY of the selected category skills
    if (formData.assignedEmployee && formData.jobCategories) {
      const employeeSkills = formData.assignedEmployee.skills || [];
      const selectedCategoryIds = formData.jobCategories.map(c => c._id || c.id);

      const hasAnyRequiredSkill = selectedCategoryIds.some(id => employeeSkills.includes(id));

      if (!hasAnyRequiredSkill && selectedCategoryIds.length > 0) {
        setFormData((prev) => ({
          ...prev,
          assignedEmployee: null,
        }));
      }
    }
  }, [formData.jobCategories]);

  // Add useEffect to fetch tasks when categories change
  useEffect(() => {
    if (formData.jobCategories && formData.jobCategories.length > 0) {
      const ids = formData.jobCategories.map(c => c._id).join(',');
      fetchTasksByCategory(ids);
    } else {
      setTasks([]);
    }
  }, [formData.jobCategories]);

  const fetchTasksByCategory = async (categoriesStr) => {
    try {
      setTaskLoading(true);
      const response = await TaskService.getTasks(1, 100, "", categoriesStr);
      if (response.success) {
        setTasks(response.data);
      } else {
        toast.error("Failed to fetch tasks");
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to fetch tasks");
    } finally {
      setTaskLoading(false);
    }
  };

  useEffect(() => {
    if (formData.customer && formData.jobCategories && formData.jobCategories.length > 0) {
      const categoryIds = formData.jobCategories.map(c => c._id).join(',');
      const customerId = formData.customer._id || formData.customer;
      fetchHistoricalTasks(customerId, categoryIds);
    } else {
      setHistoricalTasks([]);
    }
  }, [formData.customer, formData.jobCategories]);

  const fetchHistoricalTasks = async (customerId, categoryIds) => {
    try {
      const response = await JobService.getCustomerTasksHistory(customerId, categoryIds, 20);
      if (response.success) {
        // Normalize taskId to _id to be consistent with normal tasks
        const hTasks = (response.data || []).map(t => ({ ...t, _id: t.taskId }));
        setHistoricalTasks(hTasks);
      }
    } catch (error) {
      console.error("Error fetching historical tasks:", error);
    }
  };

  // Filter categories based on search term
  const filteredCategories = categories.filter(
    (category) => (category.categoryType.toLowerCase().includes(categorySearchTerm.toLowerCase()) && category.isActive)
  );

  // Filter customers based on search term
  const filteredCustomers = customers.filter(
    (customer) =>
      customer.name?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(customerSearchTerm.toLowerCase())
  );

  const filteredEmployees = employees.filter(
    (employee) =>
      employee.name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(employeeSearchTerm.toLowerCase())
  );

  console.log("Selected Categories:", formData.jobCategories);
  console.log("All Employees:", employees);

  // Function to handle task selection in a row
  const handleTaskSelect = (rowId, taskId) => {
    const updatedRows = selectedTaskRows.map(row => {
      if (row.id === rowId) {
        const task = tasks.find(t => t._id === taskId) || historicalTasks.find(t => t._id === taskId);
        return {
          ...row,
          taskId,
          cost: task?.cost || 0
        };
      }
      return row;
    });
    setSelectedTaskRows(updatedRows);
    updateTotalCost(updatedRows);
  };

  // Function to handle quantity change
  const handleQuantityChange = (rowId, newQuantity) => {
    if (newQuantity < 0) return;

    if (newQuantity === 0) {
      // Remove the row when quantity is 0
      const updatedRows = selectedTaskRows.filter(row => row.id !== rowId);

      // Ensure we always have at least one row
      if (updatedRows.length === 0) {
        updatedRows.push({ id: Date.now(), taskId: '', quantity: 1 });
      }

      setSelectedTaskRows(updatedRows);
      updateTotalCost(updatedRows);
      return;
    }

    const updatedRows = selectedTaskRows.map(row => {
      if (row.id === rowId) {
        return { ...row, quantity: newQuantity };
      }
      return row;
    });

    setSelectedTaskRows(updatedRows);
    updateTotalCost(updatedRows);
  };

  // Function to add new task row
  const addTaskRow = () => {
    if (!formData.jobCategories || formData.jobCategories.length === 0) {
      toast.error(`Please select a ${jobLabel.toLowerCase()} category first`);
      return;
    }

    if (getFilteredTasks().totalCount === 0) {
      toast.error("No tasks available to add");
      return;
    }
    if (selectedTaskRows?.length > 0 && selectedTaskRows[selectedTaskRows.length - 1].taskId === '') {
      toast.error("Please select a task to add");
      return;
    }
    setSelectedTaskRows(prev => [
      ...prev,
      { id: Date.now(), taskId: '', quantity: 1, isQuantityEnabled: true }
    ]);
  };

  // Function to update total cost
  const updateTotalCost = (updatedRows) => {
    const subtotal = updatedRows.reduce((sum, row) => {
      const task = tasks.find(t => t._id === row.taskId) || historicalTasks.find(t => t._id === row.taskId);
      return sum + ((task?.cost || 0) * row.quantity);
    }, 0);
    const taxes = (subtotal * formData.taxPercentage) / 100;
    const total = subtotal + (taxes || 0);

    setFormData(prev => ({
      ...prev,
      subtotal,
      taxes,
      totalCost: total
    }));
  };

  const validateForm = () => {
    if (!formData.jobTitle.trim()) {
      toast.error(`${jobLabel} title is required`);
      return false;
    }

    if (!formData.jobCategories || formData.jobCategories.length === 0) {
      toast.error(`Please select at least one ${jobLabel.toLowerCase()} category`);
      return false;
    }

    if (!formData.customer) {
      toast.error("Please select a customer");
      return false;
    }

    if (!formData.assignedEmployee) {
      toast.error("Please select an employee");
      return false;
    }

    if (!formData.jobDate) {
      toast.error(`${jobLabel} date is required`);
      return false;
    }

    if (!formData.jobTime.start || !formData.jobTime.end) {
      toast.error(`Please select start and end time for the ${jobLabel.toLowerCase()}`);
      return false;
    }

    // Create date objects for comparison
    const selectedDate = new Date(formData.jobDate);
    const [hours, minutes] = formData?.jobTime?.start?.split(':').map(Number);
    selectedDate.setHours(hours, minutes, 0, 0);

    const currentDate = new Date();

    if (!isEditMode && selectedDate <= currentDate) {
      toast.error(`${jobLabel} cannot be created in the past`);
      return false;
    }

    if (selectedTaskRows.length === 0) {
      toast.error("At least one task is required");
      return false;
    }

    return true;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'jobTitle') {
      if (value === ' ') {
        showTemporaryError('jobTitle', 'Cannot start with space');
        return;
      }
      const sanitizedValue = value.replace(/[^a-zA-Z0-9\s.,'-]/g, '');
      if (sanitizedValue !== value) {
        showTemporaryError('jobTitle', 'Special characters not allowed');
      }
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleTaxChange = (e) => {
    let value = e.target.value;

    if (value === '00') {
      showTemporaryError('taxPercentage', 'Invalid tax format');
      return;
    }

    // Allow only numbers and decimal point
    if (!/^\d*\.?\d*$/.test(value)) {
      showTemporaryError('taxPercentage', 'Only numbers are allowed');
      return;
    }

    // Prevent more than 2 decimal places
    if (value.includes('.') && value.split('.')[1].length > 2) {
      showTemporaryError('taxPercentage', 'Maximum 2 decimal places allowed');
      return;
    }

    // Handle empty input
    if (value === '') {
      setFormData(prev => ({
        ...prev,
        taxPercentage: '',
        taxes: 0,
        totalCost: formData.subtotal
      }));
      return;
    }

    let numValue = parseFloat(value);

    // Prevent values over 100
    if (numValue > 100) {
      value = '100';
      numValue = 100;
      showTemporaryError('taxPercentage', 'Maximum value is 100');
    }

    const taxAmount = (formData.subtotal * numValue) / 100;

    setFormData(prev => ({
      ...prev,
      taxPercentage: value,
      taxes: taxAmount,
      totalCost: formData.subtotal + taxAmount
    }));
  };

  // Add time validation function
  const validateTimes = (startTime, endTime) => {
    if (!startTime || !endTime) return true;

    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    if (startMinutes >= endMinutes) {
      showTemporaryError('time', 'Start time must be before end time');
      return false;
    }
    return true;
  };

  const handleTimeChange = (time, type) => {
    if (type === 'start') {
      // If start time changes, clear end time so user can pick a valid end time next.
      // This avoids the "start must be before end" dead-end UX when editing.
      setFormData((prev) => ({
        ...prev,
        jobTime: {
          ...prev.jobTime,
          start: time,
          end: ""
        }
      }));
      return;
    }

    // For end time, keep the validation
    if (validateTimes(formData.jobTime.start, time)) {
      setFormData((prev) => ({
        ...prev,
        jobTime: {
          ...prev.jobTime,
          end: time
        }
      }));
    }
  };

  const getLatestTasksData = () => {
    const tasksData = selectedTaskRows
      .filter(row => row.taskId)
      .map(row => ({
        taskId: row.taskId,
        quantity: row.quantity,
        isQuantityEnabled: row.isQuantityEnabled
      }));
    return tasksData;
  };

  const getJobDataPayload = (allowErrorCheck = true) => {
    const tasksData = getLatestTasksData();

    if (tasksData.length === 0 && allowErrorCheck) {
      toast.error("At least one task is required");
      throw new Error("At least one task is required");
    }
    const jobDate = new Date(formData.jobDate);

    let startDateTime, endDateTime;

    if (formData.jobTime.start && formData.jobTime.end) {
      const [startHours, startMinutes] = formData.jobTime.start.split(':').map(Number);
      startDateTime = new Date(jobDate);
      startDateTime.setHours(startHours, startMinutes, 0, 0);

      const [endHours, endMinutes] = formData.jobTime.end.split(':').map(Number);
      endDateTime = new Date(jobDate);
      endDateTime.setHours(endHours, endMinutes, 0, 0);
    }

    let jobData = {};
    if (formData.jobTitle) {
      jobData.title = formData.jobTitle;
    }
    if (formData.jobStatus) {
      jobData.status = formData.jobStatus;
    }
    if (formData.jobCategories?.length > 0) {
      jobData.categoryId = formData.jobCategories.map(c => c._id);
    }
    if (formData.assignedEmployee) {
      jobData.assignedEmployeeId = formData.assignedEmployee._id;
    }
    if (formData.customer) {
      jobData.customerId = formData.customer._id;
    }
    if (formData.jobDate) {
      jobData.scheduledDate = formData.jobDate;
    }
    if (startDateTime) {
      jobData.scheduledStartTime = startDateTime.toISOString();
    }
    if (endDateTime) {
      jobData.scheduledEndTime = endDateTime.toISOString();
    }
    jobData.showCost = formData.showCostToEmployee;
    if (tasksData.length > 0) {
      jobData.tasks = tasksData;
    }
    if (formData.subtotal && formData.taxPercentage && formData.totalCost) {
      jobData.cost = {
        subtotal: formData.subtotal,
        tax: formData.taxPercentage,
        total: formData.totalCost
      };
      if (formData.costOfSupply !== '' && formData.costOfSupply !== null && formData.costOfSupply !== undefined) {
        jobData.cost.costOfSupply = Number(formData.costOfSupply);
      }
    }

    let reoccurrenceData = null;
    if (reoccurType !== 'do_not_repeat' && !isEditMode && !isDraftMode) {
      // Set repeatTill to end of day (23:59:59)
      const repeatTillDate = repeatTill ? new Date(repeatTill) : null;
      if (repeatTillDate) {
        repeatTillDate.setHours(23, 59, 59, 999);
      }

      reoccurrenceData = {
        repeatEvery: reoccurType === 'custom' ? customRepeatUnit : reoccurType === 'daily' ? 'day' : 'week',
        repeatDifference: reoccurType === 'custom' ? customRepeatEvery : 1,
        repeatOn: reoccurType === 'weekly'
          ? [new Date().getDay()]
          : reoccurType === 'custom' && customRepeatUnit === 'week'
            ? customRepeatDays.map((selected, index) => selected ? index : -1).filter(day => day !== -1)
            : [],
        repeatTill: repeatTillDate ? repeatTillDate.toISOString() : null
      };
    }

    jobData.reoccurrence = reoccurrenceData;

    return jobData;
  };


  const saveDraft = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const jobData = getJobDataPayload(false);

      if (!jobData.title) {
        toast.error(`${jobLabel} title is required to save a draft`);
        setLoading(false);
        return;
      }

      // Only delete the original job if we're in edit mode and not cloning
      if (jobId && isEditMode && !location.pathname.includes('create-job')) {
        try {
          await JobService.deleteJob(jobId);
        } catch (error) {
          console.error("Error deleting existing job:", error);
          toast.error("Failed to delete existing job");
          setLoading(false);
          return;
        }
      }

      let response = await JobService.createDraft(jobData);

      if (response.success) {
        toast.success("Draft saved successfully");
        setSelectedTaskRows([]);
        navigate("/draft-list");
      } else {
        toast.error(response.message || "Failed to save draft");
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      if (typeof error === "string") {
        toast.error(error);
      } else {
        toast.error(error?.response?.data?.error + ": " + error?.response?.data?.details);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      let jobData = getJobDataPayload();

      if (isEditMode) {
        if (reoccurringJob && !isDraftMode) {
          // Store the job data and show modal
          setPendingJobData(jobData);
          setShowReoccurringModal(true);
          setLoading(false);
          return;
        }
        // Normal update for non-reoccurring jobs
        if (isDraftMode) {
          jobData.isDraft = false;
        }
        const response = await JobService.updateJob(jobId, jobData);
        handleUpdateResponse(response);
      } else {
        const response = await JobService.createJob(jobData);
        handleUpdateResponse(response);
      }
    } catch (error) {
      console.error("Error " + (isEditMode ? "updating" : "creating") + " job:", error);
      showApiError(
        error,
        `Failed to ${isEditMode ? "update" : "create"} ${jobLabel.toLowerCase()}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate("/job-list");
  };

  const handleDateChange = (date) => {
    if (!date) {
      setFormData(prev => ({ ...prev, jobDate: '' }));
      return;
    }

    // Set the time to noon to avoid timezone issues
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);

    const formattedDate = adjustedDate.toISOString().split('T')[0];
    setFormData(prev => ({
      ...prev,
      jobDate: formattedDate,
    }));
  };

  const formatTime = (totalMinutes) => {
    if (!totalMinutes && totalMinutes !== 0) return "N/A";

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const hourText = hours === 1 ? "hour" : "hours";
    const minuteText = minutes === 1 ? "minute" : "minutes";
    return minutes === 0
      ? `${hours} ${hourText}`
      : `${hours} ${hourText} ${minutes} ${minuteText}`;
  };

  // Get selected customer name for display
  const getSelectedCustomerName = () => {
    if (!formData.customer) return "Select customer";
    const customer = customers.find((c) => c._id === formData.customer._id);
    return customer ? customer.name : "Select customer";
  };

  // Filter tasks based on search term with null checks
  const getFilteredTasks = (searchTerm = '', rowId) => {
    // Get the current task ID for this row
    const currentRow = selectedTaskRows.find(row => row.id === rowId);
    const currentTaskId = currentRow?.taskId;

    const filterFn = (task) =>
      task &&
      task.title &&
      task.title.toLowerCase().includes((searchTerm || '').toLowerCase()) &&
      // Show all tasks except those selected in other rows
      !selectedTaskRows.find(
        row =>
          row.taskId === task._id &&
          row.id !== rowId &&
          // Don't exclude the current task if we're editing this row
          (currentTaskId !== task._id)
      );

    const filteredHistorical = Array.isArray(historicalTasks) ? historicalTasks.filter(filterFn) : [];
    const historicalIds = new Set(filteredHistorical.map(t => t._id));

    const filteredAll = Array.isArray(tasks) ? tasks.filter(task => filterFn(task) && !historicalIds.has(task._id)) : [];

    return {
      historical: filteredHistorical,
      all: filteredAll,
      totalCount: filteredHistorical.length + filteredAll.length
    };
  };

  const provideTotalCost = () => {
    const subtotal = selectedTaskRows.reduce((sum, row) => {
      const task = tasks.find(t => t._id === row.taskId) || historicalTasks.find(t => t._id === row.taskId);
      return sum + (task?.cost || 0) * row.quantity;
    }, 0);

    return `$${subtotal.toFixed(2)}`;
  };

  const getTotalCost = () => {
    const total = formData.totalCost;
    if (!total) {
      return `0.00`;
    }

    return `$${total.toFixed(2)}`;
  };

  // Add this useEffect after other useEffects
  useEffect(() => {
    // Only check for drafts in create mode, not edit mode
    if (!isEditMode && !jobId) {
      const draftExists = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (draftExists) {
        setShowDraftModal(true);
      }
    }
  }, []);

  // Add these handlers for the modal actions
  const handleLoadDraft = () => {
    loadDraftFromLocalStorage();
    setShowDraftModal(false);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setShowDraftModal(false);
  };

  const shouldRenderSaveDraftButton = () => {
    if (!isEditMode) {
      return true;
    }
    if (isDraftMode) {
      return true;
    }
    return false;
  };

  const navigateBack = () => {
    navigate(-1);
  };

  // Add these new handler functions
  const handleUpdateResponse = (response) => {
    if (response.success) {
      toast.success(isEditMode ? `${jobLabel} updated successfully!` : `${jobLabel} created successfully!`);
      navigate("/job-list");
      setSelectedTaskRows([]);
      clearDraft();
    } else {
      toast.error(response.message || `Failed to ${isEditMode ? "update" : "create"} ${jobLabel.toLowerCase()}`);
    }
  };

  const handleUpdateSingle = async () => {
    try {
      setLoading(true);
      const response = await JobService.updateJob(jobId, pendingJobData);
      handleUpdateResponse(response);
    } catch (error) {
      console.error("Error updating job:", error);
      showApiError(
        error,
        `Failed to update this ${jobLabel.toLowerCase()}`
      );
    } finally {
      setLoading(false);
      setShowReoccurringModal(false);
    }
  };

  const handleUpdateAll = async () => {
    try {
      setLoading(true);
      const jobDataWithReoccurring = {
        ...pendingJobData,
        reoccurringId: true
      };
      const response = await JobService.updateJob(jobId, jobDataWithReoccurring);
      handleUpdateResponse(response);
    } catch (error) {
      console.error("Error updating reoccurring jobs:", error);
      showApiError(
        error,
        `Failed to update all future ${jobPluralLabel.toLowerCase()}`
      );
    } finally {
      setLoading(false);
      setShowReoccurringModal(false);
    }
  };

  // Add this state to track the current row being edited
  const [currentRowId, setCurrentRowId] = useState(null);

  // Update the task input click handler
  const handleTaskInputClick = (rowId) => {
    setCurrentRowId(rowId);
    setIsTaskDropdownOpen(prev => ({
      ...prev,
      [rowId]: true
    }));
  };

  const handleQuantityEnabledToggle = (rowId) => {
    const updatedRows = selectedTaskRows.map(row => {
      if (row.id === rowId) {
        return { ...row, isQuantityEnabled: !row.isQuantityEnabled };
      }
      return row;
    });
    setSelectedTaskRows(updatedRows);
  };

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10 pb-4">
      <JobHeader
        navigateBack={navigateBack}
        isEditMode={isEditMode}
        isDraftMode={isDraftMode}
        saveDraft={saveDraft}
      />

      {showRecurrencePreview && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm"
          onClick={() => setShowRecurrencePreview(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-5xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" style={{ backgroundColor: colors.primary.headingsBody }}>
              <div>
                <h3 className="text-lg font-semibold text-white">Recurring Jobs Preview</h3>
                <p className="text-xs text-white/80 mt-0.5">
                  Preview only — jobs are not created until you save.
                </p>
              </div>
              <button
                type="button"
                className="text-white hover:text-gray-200 transition-colors bg-white/10 hover:bg-white/20 p-2 rounded-lg"
                onClick={() => setShowRecurrencePreview(false)}
                aria-label="Close preview"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4">
              {previewJobs.length === 0 ? (
                <div className="p-8 text-center text-gray-600">
                  Select a valid start date and “Repeat Till” date to preview recurring jobs.
                </div>
              ) : (
                <JobCalendar
                  jobs={previewJobs}
                  showArchived={false}
                  date={previewCalendarDate}
                  view={previewCalendarView}
                  onNavigate={(d) => setPreviewCalendarDate(d)}
                  onView={(v) => setPreviewCalendarView(v)}
                  readOnly={true}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <JobLoader />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Job Details Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-accent-light/20">
              <div className="px-6 py-4 bg-gradient-to-r from-primary-light/5 to-transparent">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <h2 className="text-lg font-semibold text-primary-headings">{`${jobLabel} Details`}</h2>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Job Title & Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Job Title Input */}
                  <div className="relative">
                    <label className={labelClasses}>
                      {`${jobLabel} Title`} {requiredStar}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="jobTitle"
                        id="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleInputChange}
                        className={inputClasses}
                        placeholder={`Enter ${jobLabel.toLowerCase()} title`}
                        maxLength={100}
                      />
                    </div>
                    <div className="flex justify-end mt-1">
                      <span className={`text-xs ${formData.jobTitle.length >= 90 ? 'text-red-500' : 'text-gray-400'}`}>
                        {formData.jobTitle.length} / 100
                      </span>
                    </div>
                    {validationMessage.jobTitle && (
                      <p className="mt-1 text-sm text-red-500 absolute">
                        {validationMessage.jobTitle}
                      </p>
                    )}
                  </div>

                  {/* Job Status (admin override) — edit mode only */}
                  {isEditMode && (
                    <div className="space-y-1">
                      <label className={labelClasses}>
                        {`${jobLabel} Status`}
                      </label>
                      <select
                        name="jobStatus"
                        value={formData.jobStatus}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, jobStatus: e.target.value }))
                        }
                        className={inputClasses}
                      >
                        {JOB_STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Category Selection */}
                <div className="relative" ref={categoryDropdownRef}>
                  <label className={labelClasses}>
                    Categories {requiredStar}
                  </label>
                  <div
                    className={`${inputClasses} flex flex-wrap gap-2 min-h-[46px] items-center py-2 cursor-text`}
                    onClick={() => setIsCategoryDropdownOpen(true)}
                  >
                    {formData.jobCategories && formData.jobCategories.map((category) => (
                      <span
                        key={category._id}
                        className="inline-flex items-center px-2.5 py-1 rounded border"
                        style={{ backgroundColor: `${colors.primary.light}20`, borderColor: `${colors.primary.light}40`, color: colors.primary.headingsBody }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {category.categoryType}
                        <button
                          type="button"
                          className="ml-1.5 flex-shrink-0 inline-flex items-center justify-center opacity-60 hover:opacity-100 focus:outline-none transition-opacity"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setFormData(prev => ({
                              ...prev,
                              jobCategories: prev.jobCategories.filter(c => c._id !== category._id)
                            }));
                          }}
                        >
                          <svg className="w-3.5 h-3.5" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                            <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                          </svg>
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={categorySearchTerm}
                      onChange={(e) => {
                        setCategorySearchTerm(e.target.value);
                      }}
                      onFocus={() => setIsCategoryDropdownOpen(true)}
                      className="flex-1 min-w-[120px] bg-transparent border-none outline-none focus:ring-0 p-0 text-sm text-primary-headings placeholder:text-accent-dark/70"
                      placeholder={formData.jobCategories?.length > 0 ? "Add more..." : "Search categories..."}
                    />

                    {formData.jobCategories?.length > 0 && (
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-accent-light/10 text-accent-dark transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, jobCategories: [] }));
                          setCategorySearchTerm('');
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {isCategoryDropdownOpen && (
                    <div className="absolute z-[100] w-full mt-1 bg-white rounded-lg shadow-lg border border-accent-light/20 max-h-64 overflow-y-auto">
                      {filteredCategories.map((category) => {
                        const isSelected = formData.jobCategories?.some(c => c._id === category._id);
                        return (
                          <div
                            key={category._id}
                            className={`p-3 cursor-pointer transition-colors duration-150 ${isSelected ? 'bg-primary-light/10 hover:bg-primary-light/20' : 'hover:bg-accent-light/5'}`}
                            onClick={() => {
                              if (isSelected) {
                                // Remove category
                                setFormData((prev) => ({
                                  ...prev,
                                  jobCategories: prev.jobCategories.filter(c => c._id !== category._id)
                                }));
                              } else {
                                // Add category
                                setFormData((prev) => ({
                                  ...prev,
                                  jobCategories: [...(prev.jobCategories || []), category]
                                }));
                                // Ensure we have at least one task row if it's the first category added
                                if (!formData.jobCategories || formData.jobCategories.length === 0) {
                                  setSelectedTaskRows([{ id: Date.now(), taskId: '', quantity: 1, isQuantityEnabled: true }]);
                                }
                              }
                              setCategorySearchTerm('');
                            }}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div
                                  className="w-8 h-8 rounded-full flex items-center justify-center"
                                  style={{
                                    backgroundColor: isSelected ? colors.primary.main : `${colors.primary.light}20`,
                                    color: isSelected ? '#ffffff' : colors.primary.headingsBody
                                  }}
                                >
                                  {isSelected ? (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <span className="font-medium">{category.categoryType.charAt(0)}</span>
                                  )}
                                </div>
                              </div>
                              <span
                                className="text-sm font-medium"
                                style={{ color: colors.primary.headingsBody }}
                              >
                                {category.categoryType}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {filteredCategories.length === 0 && (
                        <div className="text-center text-gray-500 py-2">
                          No categories found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Customer Selection */}
                <div className="relative" ref={customerDropdownRef}>
                  <label className={labelClasses}>
                    Customer {requiredStar}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={customerSearchTerm || formData.customer?.name || ''}
                      onChange={(e) => {
                        setCustomerSearchTerm(e.target.value);
                        if (formData.customer) {
                          setFormData(prev => ({ ...prev, customer: null }));
                        }
                      }}
                      onFocus={() => setIsCustomerDropdownOpen(true)}
                      className={inputClasses}
                      placeholder="Search customers..."
                    />
                    {formData.customer && (
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-accent-light/10 text-accent-dark transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, customer: null }));
                          setCustomerSearchTerm('');
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                    {isCustomerDropdownOpen && (
                      <div className="absolute z-[100] w-full mt-1 bg-white rounded-lg shadow-lg border border-accent-light/20 max-h-64 overflow-y-auto">
                        {filteredCustomers.map((customer) => (
                          <div
                            key={customer._id}
                            className="p-3 hover:bg-accent-light/5 cursor-pointer transition-colors duration-150"
                            onClick={() => {
                              setFormData((prev) => ({ ...prev, customer: customer }));
                              setCustomerSearchTerm(customer.name);
                              setIsCustomerDropdownOpen(false);
                            }}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 rounded-full bg-primary-light/20 flex items-center justify-center">
                                  <span className="text-primary-headings font-medium">
                                    {customer.name.charAt(0)}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-primary-headings">{customer.name}</p>
                                <p className="text-xs text-accent-dark">{customer.email}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                        {filteredCustomers.length === 0 && (
                          <div className="text-center text-gray-500 py-2">
                            No customers found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="relative" ref={employeeDropdownRef}>
                  <label className={labelClasses}>
                    Employee {requiredStar}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={employeeSearchTerm || formData.assignedEmployee?.name || ''}
                      onChange={(e) => {
                        setEmployeeSearchTerm(e.target.value);
                        if (formData.assignedEmployee) {
                          setFormData(prev => ({ ...prev, assignedEmployee: null }));
                        }
                      }}
                      onFocus={() => formData.jobCategories?.length > 0 ? setIsEmployeeDropdownOpen(true) : null}
                      className={inputClasses + ` ${formData.jobCategories?.length > 0 ? "" : "cursor-not-allowed opacity-50"}`}
                      placeholder={formData.jobCategories?.length > 0 ? "Search employees..." : "Select at least one category first."}
                      disabled={!formData.jobCategories || formData.jobCategories.length === 0}
                    />
                    {formData.assignedEmployee && (
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-accent-light/10 text-accent-dark transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, assignedEmployee: null }));
                          setEmployeeSearchTerm('');
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                    {isEmployeeDropdownOpen && (
                      <div className="absolute z-[100] w-full mt-1 bg-white rounded-lg shadow-lg border border-accent-light/20 max-h-64 overflow-y-auto">
                        {filteredEmployees.map((employee) => (
                          <div
                            key={employee._id}
                            className="p-3 hover:bg-accent-light/5 cursor-pointer transition-colors duration-150"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log(">>>> Employee:", employee);
                              setFormData((prev) => ({ ...prev, assignedEmployee: employee }));
                              setEmployeeSearchTerm('');
                              setIsEmployeeDropdownOpen(false);
                            }}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 rounded-full bg-primary-light/20 flex items-center justify-center">
                                  <span className="text-primary-headings font-medium">
                                    {employee.name.charAt(0)}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-primary-headings">{employee.name}</p>
                                <p className="text-xs text-accent-dark">{employee.email}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                        {filteredEmployees.length === 0 && (
                          <div className="text-center text-gray-500 py-2">
                            No employees found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Date and Time Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="relative">
                    <label className={labelClasses}>
                      Date {requiredStar}
                    </label>
                    <div className="relative">
                      <DatePicker
                        selected={formData.jobDate ? (() => {
                          const date = new Date(formData.jobDate);
                          date.setHours(12, 0, 0, 0);
                          return date;
                        })() : null}
                        onChange={(date) => handleDateChange(date)}
                        dateFormat="yyyy-MM-dd"
                        minDate={new Date()}
                        placeholderText="Select date"
                        className={`${inputClasses} pr-12`}
                        wrapperClassName="w-full"
                        calendarClassName="!w-[360px] shadow-lg border border-accent-light/20 rounded-lg"
                        showPopperArrow={false}
                        dayClassName={() => "!w-[45px] !h-[45px] !leading-[45px]"}
                        monthClassName={() => "!text-base"}
                        weekDayClassName={() => "!w-[45px] !h-[45px] !leading-[45px]"}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-dark pointer-events-none">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative" ref={startTimePickerRef}>
                      <label className={labelClasses}>
                        Start Time {requiredStar}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          className={`${inputClasses} pr-12`}
                          placeholder="Select start time"
                          value={formData?.jobTime?.start || ''}
                          readOnly
                          onClick={() => setIsStartTimeOpen(!isStartTimeOpen)}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-dark pointer-events-none">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        {isStartTimeOpen && (
                          <TimeDropdown
                            value={formData?.jobTime?.start || ''}
                            onChange={(time) => handleTimeChange(time, 'start')}
                            onClose={() => setIsStartTimeOpen(false)}
                          />
                        )}
                        {validationMessage.time && (
                          <p className="mt-1 text-sm text-red-500 absolute">
                            {validationMessage.time}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="relative" ref={endTimePickerRef}>
                      <label className={labelClasses}>
                        End Time {requiredStar}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          className={`${inputClasses} pr-12`}
                          placeholder="Select end time"
                          value={formData?.jobTime?.end || ''}
                          readOnly
                          onClick={() => setIsEndTimeOpen(!isEndTimeOpen)}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-dark pointer-events-none">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        {isEndTimeOpen && (
                          <TimeDropdown
                            value={formData?.jobTime?.end || ''}
                            onChange={(time) => handleTimeChange(time, 'end')}
                            onClose={() => setIsEndTimeOpen(false)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {!isEditMode && !isDraftMode && (
                  <div className="flex flex-col space-y-2">
                    <label className={labelClasses}>
                      Reoccur
                    </label>
                    <div className="space-y-4">
                      <div className="relative" ref={reoccurTypeDropdownRef}>
                        <div
                          className="relative cursor-pointer"
                          onClick={() => setIsReoccurTypeDropdownOpen(!isReoccurTypeDropdownOpen)}
                        >
                          <input
                            type="text"
                            readOnly
                            value={(() => {
                              switch (reoccurType) {
                                case 'daily':
                                  return 'Daily';
                                case 'weekly':
                                  return `Weekly on ${getDayName(new Date().getDay())}`;
                                case 'custom':
                                  return 'Custom';
                                default:
                                  return 'Do not repeat';
                              }
                            })()}
                            className={`${inputClasses} cursor-pointer`}
                            placeholder="Select recurrence type"
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-dark pointer-events-none">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>

                        {isReoccurTypeDropdownOpen && (
                          <div className="absolute z-[100] w-full mt-1 bg-white rounded-lg shadow-lg border border-accent-light/20 overflow-hidden">
                            {[
                              {
                                value: 'do_not_repeat',
                                label: 'Do not repeat',
                                icon: 'M6 18L18 6M6 6l12 12',
                                example: 'The job will be created only once on the selected date.',
                                warning: null
                              },
                              {
                                value: 'daily',
                                label: 'Daily',
                                icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
                                example: 'e.g. If the job date is Mar 10, jobs will be auto-created on Mar 11, 12, 13… until the end date.',
                                warning: 'This will create a new job every single day. Ensure the "Repeat Till" date is correct to avoid creating too many jobs.'
                              },
                              {
                                value: 'weekly',
                                label: `Weekly on ${getDayName(new Date().getDay())}`,
                                icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
                                example: `e.g. Repeats every ${getDayName(new Date().getDay())}. The day is fixed to today's day of the week.`,
                                warning: `The recurrence day is locked to ${getDayName(new Date().getDay())}. To repeat on a different day, use "Custom" instead.`
                              },
                              {
                                value: 'custom',
                                label: 'Custom',
                                icon: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4',
                                example: 'e.g. Every 2 weeks on Mon & Wed, or every 3 days — fully configurable.',
                                warning: 'Maximum repeat duration is 3 months from the job date. Jobs beyond this window will not be created.'
                              }
                            ].map((option) => (
                              <div
                                key={option.value}
                                className="relative p-3 hover:bg-accent-light/5 cursor-pointer transition-colors duration-150"
                                onClick={() => {
                                  setReoccurType(option.value);
                                  if (option.value === 'weekly') {
                                    const today = new Date().getDay();
                                    setCustomRepeatDays(Array(7).fill(false).map((_, index) => index === today));
                                  }
                                  setIsReoccurTypeDropdownOpen(false);
                                  setRecurrenceTooltip(null);
                                }}
                              >
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0">
                                    <div className="w-8 h-8 rounded-full bg-primary-light/20 flex items-center justify-center">
                                      <svg className="w-4 h-4 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={option.icon} />
                                      </svg>
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-primary-headings">
                                      {option.label}
                                    </span>
                                  </div>
                                  {/* Tooltip trigger icon */}
                                  <button
                                    type="button"
                                    className="ml-1 p-1 rounded-full text-accent-dark/50 hover:text-primary-headings hover:bg-primary-light/10 transition-colors flex-shrink-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRecurrenceTooltip(recurrenceTooltip === option.value ? null : option.value);
                                    }}
                                    title="Learn more"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  </button>
                                  {reoccurType === option.value && (
                                    <svg className="w-5 h-5 text-primary-headings flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                                {/* Inline tooltip panel */}
                                {recurrenceTooltip === option.value && (
                                  <div
                                    className="mt-2 mx-1 rounded-lg border overflow-hidden"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {/* Example row */}
                                    <div className="flex items-start space-x-2 px-3 py-2 bg-blue-50 border-b border-blue-100">
                                      <svg className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      <p className="text-xs text-blue-700 leading-relaxed">{option.example}</p>
                                    </div>
                                    {/* Warning row */}
                                    {option.warning && (
                                      <div className="flex items-start space-x-2 px-3 py-2 bg-amber-50">
                                        <svg className="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        <p className="text-xs text-amber-700 leading-relaxed">{option.warning}</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {reoccurType === 'custom' && (
                        <div className="space-y-4 p-4 bg-accent-light/5 rounded-lg border border-accent-light/20">
                          {/* Repeat Every Section */}
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-accent-dark">Repeat every</label>
                            <div className="flex items-center space-x-3">
                              <input
                                type="number"
                                value={customRepeatEvery}
                                onChange={(e) => setCustomRepeatEvery(e.target.value)}
                                onBlur={() => setCustomRepeatEvery(Math.max(1, parseInt(customRepeatEvery) || 1))}
                                className={`${inputClasses} w-24`}
                              />
                              <div className="relative flex-grow" ref={customRepeatUnitDropdownRef}>
                                <div
                                  className="relative cursor-pointer"
                                  onClick={() => setIsCustomRepeatUnitDropdownOpen(!isCustomRepeatUnitDropdownOpen)}
                                >
                                  <input
                                    type="text"
                                    readOnly
                                    value={customRepeatUnit === 'day' ? 'day' : customRepeatUnit === 'week' ? 'week' : 'month'}
                                    className={`${inputClasses} cursor-pointer`}
                                    placeholder="Select unit"
                                  />
                                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-dark pointer-events-none">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </div>
                                </div>

                                {isCustomRepeatUnitDropdownOpen && (
                                  <div className="absolute z-[100] w-full mt-1 bg-white rounded-lg shadow-lg border border-accent-light/20 overflow-hidden">
                                    {[
                                      { value: 'day', label: 'day', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                                      { value: 'week', label: 'week', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                                      { value: 'month', label: 'month', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' }
                                    ].map((option) => (
                                      <div
                                        key={option.value}
                                        className="p-3 hover:bg-accent-light/5 cursor-pointer transition-colors duration-150"
                                        onClick={() => {
                                          setCustomRepeatUnit(option.value);
                                          setIsCustomRepeatUnitDropdownOpen(false);
                                        }}
                                      >
                                        <div className="flex items-center space-x-3">
                                          <div className="flex-shrink-0">
                                            <div className="w-8 h-8 rounded-full bg-primary-light/20 flex items-center justify-center">
                                              <svg className="w-4 h-4 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={option.icon} />
                                              </svg>
                                            </div>
                                          </div>
                                          <div className="flex-1">
                                            <span className="text-sm font-medium text-primary-headings">
                                              {option.label}
                                            </span>
                                          </div>
                                          {customRepeatUnit === option.value && (
                                            <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Repeat On Section - Only show for weekly repetition */}
                          {customRepeatUnit === 'week' && (
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-accent-dark">Repeat on</label>
                              <div className="flex flex-wrap gap-2">
                                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                                  <button
                                    key={index}
                                    type="button"
                                    onClick={() => {
                                      const newDays = [...customRepeatDays];
                                      newDays[index] = !newDays[index];
                                      setCustomRepeatDays(newDays);
                                    }}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${customRepeatDays[index]
                                      ? 'bg-primary-headings text-white'
                                      : 'bg-accent-light/10 text-accent-dark hover:bg-accent-light/20'
                                      }`}
                                  >
                                    {day}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {reoccurType !== 'do_not_repeat' && (
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-accent-dark">Repeat Till</label>
                          <div className="relative">
                            <DatePicker
                              selected={repeatTill}
                              onChange={(date) => setRepeatTill(date)}
                              minDate={formData.jobDate ? new Date(formData.jobDate) : new Date()}
                              maxDate={getMaxRepeatDate()}
                              dateFormat="yyyy-MM-dd"
                              placeholderText="Select end date"
                              className={`${inputClasses} pr-12`}
                              wrapperClassName="w-full"
                              calendarClassName="!w-[360px] shadow-lg border border-accent-light/20 rounded-lg"
                              showPopperArrow={false}
                              dayClassName={() => "!w-[45px] !h-[45px] !leading-[45px]"}
                              monthClassName={() => "!text-base"}
                              weekDayClassName={() => "!w-[45px] !h-[45px] !leading-[45px]"}
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-dark pointer-events-none">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          </div>
                          <p className="text-xs text-accent-dark/70">Maximum 3 months from the job date</p>
                        </div>
                      )}

                      {/* Recurrence summary banner — shown after a recurrence option is selected */}
                      {reoccurType !== 'do_not_repeat' && (() => {
                        const count = getEstimatedJobCount();
                        const hasCount = count !== null && count > 0;

                        const infoText = {
                          daily: `Jobs will be created once per day from the job date to the "Repeat Till" date${hasCount ? ` — that's ${count} job${count !== 1 ? 's' : ''} in total` : ''
                            }.`,
                          weekly: `Jobs will repeat every ${getDayName(new Date().getDay())} until the "Repeat Till" date${hasCount ? ` — that's ${count} job${count !== 1 ? 's' : ''} in total` : ''
                            }.`,
                          custom: `Jobs will be created based on your custom interval${hasCount ? `. Based on your settings, this will create ${count} job${count !== 1 ? 's' : ''} in total` : ''
                            }.`
                        }[reoccurType];

                        const warnText = {
                          daily: hasCount
                            ? `This will create ${count} job${count !== 1 ? 's' : ''}. Please confirm you want to proceed with this many recurring jobs.`
                            : 'Select a "Repeat Till" date to see how many jobs will be created.',
                          weekly: hasCount
                            ? `This will create ${count} job${count !== 1 ? 's' : ''} (every ${getDayName(new Date().getDay())}). The recurrence day is locked to today — use Custom to change it.`
                            : 'Select a "Repeat Till" date to see how many jobs will be created.',
                          custom: hasCount
                            ? `This will create ${count} job${count !== 1 ? 's' : ''} based on your current interval settings. Maximum allowed is 3 months from the job date.`
                            : 'Select a "Repeat Till" date to see how many jobs will be created.'
                        }[reoccurType];

                        return (
                          <div className="rounded-lg border border-amber-200 overflow-hidden">
                            <div className="flex items-start justify-between gap-3 px-3 py-2 bg-blue-50 border-b border-blue-100">
                              <svg className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-blue-700">{infoText}</p>
                              </div>
                              {hasCount && (
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-headings bg-white/70 hover:bg-white px-2.5 py-1.5 rounded-md border border-blue-200 shadow-sm transition-colors whitespace-nowrap"
                                  onClick={() => {
                                    setShowRecurrencePreview(true);
                                    const seed = formData?.jobDate ? new Date(formData.jobDate) : new Date();
                                    if (!isNaN(seed.getTime())) setPreviewCalendarDate(seed);
                                    setPreviewCalendarView('month');
                                  }}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  Preview
                                </button>
                              )}
                            </div>
                            <div className="flex items-start space-x-2 px-3 py-2 bg-amber-50">
                              <svg className="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <p className="text-xs text-amber-700 font-medium">{warnText}</p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                <div className="flex flex-col space-y-2">
                  <label className={labelClasses}>
                    Cost Visibility
                  </label>
                  <div className="relative flex items-center space-x-3 p-3 bg-accent-light/5 rounded-lg border border-accent-light/20">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-primary-headings">Show Cost to Employee</p>
                      <p className="text-xs text-accent-dark mt-0.5">When enabled, the employee will be able to see the job cost details</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        showCostToEmployee: !prev.showCostToEmployee
                      }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.showCostToEmployee ? 'bg-primary-headings' : 'bg-accent-light/30'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.showCostToEmployee ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Tasks Selection */}
            <div className="bg-white rounded-2xl shadow-sm border border-accent-light/20">
              <div className="px-6 py-4 bg-gradient-to-r from-primary-light/5 to-transparent">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <h2 className="text-lg font-semibold text-primary-headings">Select Tasks</h2>
                  </div>
                  <button
                    type="button"
                    onClick={addTaskRow}
                    className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-primary-light/10 text-primary-headings hover:bg-primary-light/20 transition-colors"
                  >
                    <span className="text-sm font-medium">
                      {selectedTaskRows.length > 1 || (selectedTaskRows.length === 1 && selectedTaskRows[0].taskId)
                        ? `Add Another Task to ${jobLabel}`
                        : `Add Task to ${jobLabel}`}
                    </span>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {(!formData.jobCategories || formData.jobCategories.length === 0) ? (
                  <div className="text-center py-8 px-4">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-light/20 flex items-center justify-center">
                      <svg className="w-8 h-8 text-accent-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-accent-dark font-medium">Please select a category first</p>
                    <p className="text-sm text-accent-dark/60 mt-1">Tasks will be displayed based on the selected category</p>
                  </div>
                ) : taskLoading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-primary-light border-t-transparent"></div>
                    <p className="mt-2 text-accent-dark">Loading tasks...</p>
                  </div>
                ) : tasks.length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-light/20 flex items-center justify-center">
                      <svg className="w-8 h-8 text-accent-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <p className="text-accent-dark font-medium">No tasks available</p>
                    <p className="text-sm text-accent-dark/60 mt-1">There are no tasks for the selected category</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedTaskRows.map((row) => (
                      <div
                        key={row.id}
                        className="flex items-center space-x-4 p-4 rounded-lg border border-accent-light/20 bg-accent-light/5"
                        ref={el => taskDropdownRefs.current[row.id] = el}
                      >
                        <div className="flex-grow relative">
                          <div className="relative">
                            <input
                              type="text"
                              value={taskSearchTerms[row.id] || tasks.find(task => task._id === row.taskId)?.title || ''}
                              onChange={(e) => {
                                setTaskSearchTerms(prev => ({
                                  ...prev,
                                  [row.id]: e.target.value
                                }));
                                // Clear the selected task if user is typing
                                if (row.taskId) {
                                  const updatedRows = selectedTaskRows.map(r => {
                                    if (r.id === row.id) {
                                      return { ...r, taskId: '' };
                                    }
                                    return r;
                                  });
                                  setSelectedTaskRows(updatedRows);
                                }
                              }}
                              onClick={() => handleTaskInputClick(row.id)}
                              className={`${inputClasses} pr-10`}
                              placeholder="Search and select a task..."
                            />
                            <div
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-dark cursor-pointer"
                              onClick={() => setIsTaskDropdownOpen(prev => ({
                                ...prev,
                                [row.id]: !prev[row.id]
                              }))}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>

                          {isTaskDropdownOpen[row.id] && (() => {
                            const { historical, all, totalCount } = getFilteredTasks(taskSearchTerms[row.id], row.id);
                            return (
                              <div className="absolute z-[100] w-full mt-1 bg-white rounded-lg shadow-lg border border-accent-light/20 max-h-64 overflow-y-auto p-2">
                                {totalCount > 0 ? (
                                  <>
                                    {historical.length > 0 && (
                                      <div className="mb-2">
                                        <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 rounded-md">
                                          Previously Used
                                        </div>

                                        {Object.entries(
                                          historical.reduce((acc, t) => {
                                            const key = (t?.categoryName || "Uncategorized").trim() || "Uncategorized";
                                            acc[key] = acc[key] || [];
                                            acc[key].push(t);
                                            return acc;
                                          }, {})
                                        )
                                          .sort(([a], [b]) => a.localeCompare(b))
                                          .map(([categoryName, tasksInCategory]) => (
                                            <div key={categoryName} className="mt-3">
                                              <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-primary-light/5 border border-primary-light/15">
                                                <div className="flex items-center gap-2 min-w-0">
                                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-primary-headings/10 text-primary-headings text-xs font-bold">
                                                    {categoryName.slice(0, 1).toUpperCase()}
                                                  </span>
                                                  <span className="text-xs font-semibold text-primary-headings truncate">
                                                    {categoryName}
                                                  </span>
                                                </div>
                                                <span className="text-[11px] font-semibold text-primary-headings/70 bg-white px-2 py-0.5 rounded-full border border-primary-light/20">
                                                  {tasksInCategory.length}
                                                </span>
                                              </div>

                                              <div className="mt-2 space-y-1">
                                                {tasksInCategory.map((task) => (
                                                  <button
                                                    type="button"
                                                    key={task._id}
                                                    className="w-full text-left rounded-lg border border-accent-light/20 hover:border-primary-light/40 hover:bg-accent-light/5 transition-colors"
                                                    onClick={() => {
                                                      handleTaskSelect(row.id, task._id);
                                                      setTaskSearchTerms(prev => ({
                                                        ...prev,
                                                        [row.id]: task.title || ''
                                                      }));
                                                      setIsTaskDropdownOpen(prev => ({
                                                        ...prev,
                                                        [row.id]: false
                                                      }));
                                                    }}
                                                  >
                                                    <div className="px-3 py-2.5">
                                                      <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                          <div className="font-medium text-primary-headings truncate">
                                                            {task.title}
                                                          </div>
                                                          <div className="mt-0.5 text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
                                                            {task.timesUsed ? (
                                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                                                Used {task.timesUsed} {task.timesUsed === 1 ? 'time' : 'times'}
                                                              </span>
                                                            ) : null}
                                                          </div>
                                                        </div>
                                                        {(task.cost !== undefined && task.cost > 0) && (
                                                          <span className="shrink-0 text-sm font-semibold text-cta">
                                                            ${task.cost}
                                                          </span>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </button>
                                                ))}
                                              </div>
                                            </div>
                                          ))}
                                      </div>
                                    )}

                                    {all.length > 0 && (
                                      <div>
                                        {historical.length > 0 && (
                                          <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 rounded-md mt-2 mb-1">
                                            All Tasks
                                          </div>
                                        )}
                                        {Object.entries(
                                          all.reduce((acc, t) => {
                                            const key = (t?.category?.categoryType || t?.categoryName || "Uncategorized").trim() || "Uncategorized";
                                            acc[key] = acc[key] || [];
                                            acc[key].push(t);
                                            return acc;
                                          }, {})
                                        )
                                          .sort(([a], [b]) => a.localeCompare(b))
                                          .map(([categoryName, tasksInCategory]) => (
                                            <div key={categoryName} className="mt-3">
                                              <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-primary-light/5 border border-primary-light/15">
                                                <div className="flex items-center gap-2 min-w-0">
                                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-primary-headings/10 text-primary-headings text-xs font-bold">
                                                    {categoryName.slice(0, 1).toUpperCase()}
                                                  </span>
                                                  <span className="text-xs font-semibold text-primary-headings truncate">
                                                    {categoryName}
                                                  </span>
                                                </div>
                                                <span className="text-[11px] font-semibold text-primary-headings/70 bg-white px-2 py-0.5 rounded-full border border-primary-light/20">
                                                  {tasksInCategory.length}
                                                </span>
                                              </div>

                                              <div className="mt-2 space-y-1">
                                                {tasksInCategory.map((task) => (
                                                  <button
                                                    type="button"
                                                    key={task._id}
                                                    className="w-full text-left rounded-lg border border-accent-light/20 hover:border-primary-light/40 hover:bg-accent-light/5 transition-colors"
                                                    onClick={() => {
                                                      handleTaskSelect(row.id, task._id);
                                                      setTaskSearchTerms(prev => ({
                                                        ...prev,
                                                        [row.id]: task.title || ''
                                                      }));
                                                      setIsTaskDropdownOpen(prev => ({
                                                        ...prev,
                                                        [row.id]: false
                                                      }));
                                                    }}
                                                  >
                                                    <div className="px-3 py-2.5">
                                                      <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                          <div className="font-medium text-primary-headings truncate">
                                                            {task.title}
                                                          </div>
                                                        </div>
                                                        {(task.cost !== undefined && task.cost > 0) && (
                                                          <span className="shrink-0 text-sm font-semibold text-cta">
                                                            ${task.cost}
                                                          </span>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </button>
                                                ))}
                                              </div>
                                            </div>
                                          ))}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="p-4 text-center text-accent-dark">
                                    <p className="font-medium">No tasks available</p>
                                    <p className="text-sm text-accent-dark/60 mt-1">All tasks for this category are selected in other rows</p>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center space-x-3">
                          <button
                            type="button"
                            onClick={() => handleQuantityChange(row.id, row.quantity - 1)}
                            disabled={!row.taskId}
                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-accent-light/10 text-accent-dark transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                          </button>

                          <div className="w-8 h-8 rounded-full bg-primary-light/10 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-headings">
                              {row.quantity}
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleQuantityChange(row.id, row.quantity + 1)}
                            disabled={!row.taskId}
                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-accent-light/10 text-accent-dark transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>

                        {/* Quantity Enabled Toggle */}
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => handleQuantityEnabledToggle(row.id)}
                            disabled={!row.taskId}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${row.isQuantityEnabled
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            title={row.isQuantityEnabled ? 'Quantity enabled' : 'Quantity disabled'}
                          >
                            {row.isQuantityEnabled ? 'Qty On' : 'Qty Off'}
                          </button>
                        </div>

                        {/* Delete button */}
                        {selectedTaskRows.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleQuantityChange(row.id, 0)}
                            className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Cost Summary */}
            <div className="bg-white rounded-2xl shadow-sm border border-accent-light/20">
              <div className="px-6 py-4 bg-gradient-to-r from-primary-light/5 to-transparent">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h2 className="text-lg font-semibold text-primary-headings">Cost Summary</h2>
                </div>
              </div>

              <div className="p-6">
                <div className="bg-accent-light/5 rounded-xl p-5 border border-accent-light/10">
                  <div className={`grid grid-cols-1 ${provideTotalCost() === '$0.00' && formData.taxes === 0 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-6`}>
                    {provideTotalCost() !== '$0.00' && (
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-accent-dark mb-1">Subtotal</label>
                        <div className="flex items-center space-x-2">
                          <div className="w-10 h-10 rounded-full bg-primary-light/10 flex items-center justify-center">
                            <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m-8-6h16" />
                            </svg>
                          </div>
                          <div className="text-2xl font-bold text-primary-headings">{provideTotalCost()}</div>
                        </div>
                        <p className="text-xs text-accent-dark/70 mt-1">Sum of all selected tasks</p>
                      </div>
                    )}

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-accent-dark mb-1">Tax Rate (%)</label>
                      <div className="relative">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-primary-light/10 flex items-center justify-center mr-2">
                            <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <input
                            type="text"
                            name="taxPercentage"
                            value={formData.taxPercentage || ''}
                            onChange={handleTaxChange}
                            className="w-full px-4 py-2.5 bg-white border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent"
                            placeholder="Enter tax percentage"
                          />
                        </div>
                        <div className="mt-1 text-xs text-accent-dark/70">
                          Tax Amount: <span className="font-medium">${(formData.taxes || 0).toFixed(2)}</span>
                        </div>
                        {validationMessage.taxPercentage && (
                          <p className="mt-1 text-sm text-red-500 absolute">
                            {validationMessage.taxPercentage}
                          </p>
                        )}
                      </div>
                    </div>

                    {getTotalCost() !== '$0.00' && (
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-accent-dark mb-1">{`Total ${chargeOutRateTitle}`}</label>
                        <div className="flex items-center space-x-2">
                          <div className="w-10 h-10 rounded-full bg-cta/10 flex items-center justify-center">
                            <svg className="w-5 h-5 text-cta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="text-2xl font-bold text-cta">{getTotalCost()}</div>
                        </div>
                        <p className="text-xs text-accent-dark/70 mt-1">Subtotal + Tax</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-6">
                    <div className="flex flex-col md:max-w-md">
                      <label className="text-sm font-medium text-accent-dark mb-1">{`${costOfSupplyTitle} ($)`}</label>
                      <div className="relative">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-primary-light/10 flex items-center justify-center mr-2">
                            <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <input
                            type="number"
                            name="costOfSupply"
                            value={formData.costOfSupply}
                            onChange={(e) => {
                              const value = Math.max(0, Number(e.target.value));
                              setFormData(prev => ({ ...prev, costOfSupply: value === 0 && e.target.value === '' ? '' : value }));
                            }}
                            className="w-1/2 px-4 py-2.5 bg-white border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent"
                            placeholder="Optional"
                          />
                        </div>
                        <p className="mt-1 text-xs text-accent-dark/70">
                          Optional internal cost field. Leave blank if not applicable.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional cost information card */}
                <div className="mt-4 p-4 bg-primary-light/5 rounded-lg border border-primary-light/20">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-primary-headings font-medium">Cost Calculation</p>
                      <p className="text-xs text-accent-dark mt-1">
                        The total cost is calculated based on the tasks selected, their quantities, and the applied tax rate.
                        {formData.showCostToEmployee ?
                          " This information will be visible to the assigned employee." :
                          " This information will be hidden from the assigned employee."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons - Now part of normal flow */}
            <div className="mt-8 border-t border-accent-light/20 pt-6">
              <div className="flex justify-end items-center">
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-cta text-white rounded-lg hover:bg-cta/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cta transition-colors duration-200"
                >
                  {isEditMode ? isDraftMode ? `Create ${jobLabel}` : `Update ${jobLabel}` : `Create ${jobLabel}`}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Draft Modal */}
      {showDraftModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" aria-hidden="true"></div>

            {/* Modal panel */}
            <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-light/20">
                    <svg className="w-6 h-6 text-primary-headings" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium leading-6 text-primary-headings">
                    Draft Found
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-accent-dark">
                      A draft {jobLabel.toLowerCase()} was found. Would you like to load it?
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  onClick={handleDiscardDraft}
                  className="px-4 py-2 text-sm font-medium text-accent-dark bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-light"
                >
                  No, Discard
                </button>
                <button
                  type="button"
                  onClick={handleLoadDraft}
                  className="px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md bg-primary-headings hover:bg-primary-headings/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
                >
                  Yes, Load Draft
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showReoccurringModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" aria-hidden="true"></div>

            {/* Modal panel */}
            <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <button
                type="button"
                onClick={() => setShowReoccurringModal(false)}
                className="absolute top-3 right-3 p-2 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-light/20">
                    <svg className="w-6 h-6 text-primary-headings" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium leading-6 text-primary-headings">
                    {`Update Reoccurring ${jobLabel}`}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-accent-dark">
                      Would you like to update all future instances of this reoccurring {jobLabel.toLowerCase()}, or just this one?
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  onClick={handleUpdateSingle}
                  className="px-4 py-2 text-sm font-medium text-accent-dark bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-light"
                >
                  {`Only This ${jobLabel}`}
                </button>
                <button
                  type="button"
                  onClick={handleUpdateAll}
                  className="px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md bg-primary-headings hover:bg-primary-headings/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
                >
                  {`All Future ${jobPluralLabel}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateJob;
